import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {ReplaySubject} from 'rxjs';
import {UserPermissions} from '../../../../shared/interface/user-permissions';
import {InsuranceCompany} from '../../../../shared/interface/insurance-company';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {AuthService} from '../../../../shared/services/auth.service';
import {FilterService} from '../../../../shared/services/filter.service';
import {ActivatedRoute, Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {StoreRootState} from '../../../../shared/ngrx/reducers';
import {delay, distinctUntilChanged, takeUntil, tap} from 'rxjs/operators';
import {CountedAndPaginated} from '../../../../shared/interface/response/counted-and-paginated';
import {getCurrentRouteState} from '../../../../shared/ngrx/selector';
import {RouterStateUrl} from '../../../../shared/ngrx/route-serializer';
import {Uuid} from '../../../../shared/interface/response/uuid';
import {JobResultDialogComponent} from '../../../../shared/components/job-result-dialog/job-result-dialog.component';
import {HttpErrorResponse} from '@angular/common/http';
import {ConfirmDialogComponent} from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import {ThresholdDictionary} from '../../../../shared/interface/threshold-dictionary';
import {ThresholdService} from '../../../../shared/services/threshold.service';
import {EditThresholdModalComponent} from '../edit-threshold-modal/edit-threshold-modal.component';
import {animate, state, style, transition, trigger} from '@angular/animations';

@Component({
  selector: 'app-all-thresholds-list',
  templateUrl: './all-thresholds-list.component.html',
  styleUrls: ['./all-thresholds-list.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({height: '0px', minHeight: '0'})),
      state('expanded', style({height: '*'})),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class AllThresholdsListComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild('paginator') paginator: MatPaginator;

  initialFilters: any;
  items: ThresholdDictionary[] = [];
  displayedColumns: string[] = ['name', 'logo', 'insuranceCompany', 'importer', 'insuranceCompanyType', 'policyTypes', 'actions'];
  totalData = 0;
  userPermissions: ReplaySubject<UserPermissions>;
  selectedInsuranceCompanies: InsuranceCompany[] = [];
  expandedElement: ThresholdDictionary = null;
  saveExpandedElement: ThresholdDictionary = null;
  modify = false;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private thresholdService: ThresholdService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private auth: AuthService,
    private filterService: FilterService,
    private activatedRoute: ActivatedRoute,
    private store: Store<StoreRootState>,
    private router: Router,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.thresholdService.thresholdDictionaryListSubject
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<ThresholdDictionary>) => {
          this.items = e.items;
          this.totalData = e.pagination.total;

          if (this.initialFilters?.uuid) {
            this.expandElement(this.items[0]);
          }

          if (this.saveExpandedElement) {
            this.items.forEach((thresholdDictionary: ThresholdDictionary) => {
              if (thresholdDictionary.uuid === this.saveExpandedElement.uuid) {
                this.expandedElement = thresholdDictionary;
              }
            });
            this.saveExpandedElement = null;
          }
        })
      ).subscribe();
    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      uuid: 'string',
      name: 'string',
      in$insuranceCompany: 'int[]',
      in$importer: 'int[]',
      group: 'string',
      risk: 'string',
      policyType: 'int[]',
      internalRisk: 'int[]',
    }, filters);
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: event.pageIndex,
        pageSize: event.pageSize,
        ...this.filtersForm.value
      })]);
    });

    this.subscribeQueryParams();
  }

  subscribeQueryParams(): void {
    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((routerState: RouterStateUrl) => {
        this.parseFilters(routerState.params);
        this.paginator.pageIndex = routerState.params.page;
        this.paginator.pageSize = routerState.params.pageSize;
        this.loadListData();
      })
    ;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  loadListData(): void {
    this.thresholdService.loadList(this.paginator.pageIndex, this.paginator.pageSize, this.buildFilters());
  }

  buildFilters(): any {
    return this.initialFilters;
  }

  filter(): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      page: 0,
      pageSize: this.paginator.pageSize,
      ...this.filtersForm.value,
    })]);
  }

  downloadReport(): void {
    this.thresholdService.downloadReport(this.initialFilters)
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator.pageSize,
    }]);
  }

  delete(rc: ThresholdDictionary): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć próg?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.thresholdService.deleteConfiguration(rc.uuid)
          .subscribe({
            next: () => {
              this.snack.open('Pomyślnie usunięto próg', '', {duration: 2000});
              this.loadListData();
            },
            error: (error: HttpErrorResponse) => {
              this.snack.open('Przy usuwaniu wystąpił błąd', error.status.toString(), {duration: 2000});
            }
          });
      }
    });
  }

  edit(td: ThresholdDictionary): void {
    const dialogRef = this.dialog.open(EditThresholdModalComponent, {
      width: '90vw',
      data: {thresholdDictionary: td},
      maxHeight: '90vh',
    });

    dialogRef.afterClosed().subscribe(() => {
      this.loadListData();
    });
  }

  expandElement(thresholdDictionary: ThresholdDictionary): void {
    this.expandedElement = this.expandedElement === thresholdDictionary ? null : thresholdDictionary;
    this.modify = false;
  }

  modificationClosed(): void {
    this.saveExpandedElement = this.expandedElement;
    this.loadListData();
  }

}
